import React, { ReactNode } from "react"
import { FormattedMessage } from "react-intl"
import {
  Chip,
  Box,
  Typography,
  Tile,
  IconWithBackground,
  CreditCardIcon,
  TileProps,
  IconWithBackgroundProps
} from "@carrotfertility/carotene-core"
import CardStates from "#/utils/CardStates"

const activeStatusChip = <Chip size="small" color="success" label={<FormattedMessage defaultMessage="Active" />} />
const shippedStatusChip = <Chip size="small" color="info" label={<FormattedMessage defaultMessage="Shipped" />} />
const lockedStatusChip = <Chip size="small" color="error" label={<FormattedMessage defaultMessage="Locked" />} />
const pendingStatusChip = <Chip size="small" color="info" label={<FormattedMessage defaultMessage="Requested" />} />
const preAuthStatusChip = (
  <Chip
    color="success"
    size="small"
    label={
      <FormattedMessage
        defaultMessage="Ready to authorize"
        description="Status chip indicating user can submit a pre-authorization"
      />
    }
  />
)
const carrotCardStatusByStates: Partial<Record<CardStates, ReactNode>> = {
  [CardStates.ACTIVE]: activeStatusChip,
  [CardStates.SHIPPED]: shippedStatusChip,
  [CardStates.LOCKED]: lockedStatusChip,
  [CardStates.DEDUCTIBLE_NOT_MET]: lockedStatusChip,
  [CardStates.NEEDS_ENROLLMENT]: lockedStatusChip,
  [CardStates.RESTRICTION_PERIOD]: lockedStatusChip,
  [CardStates.PENDING]: pendingStatusChip,
  [CardStates.PRE_AUTH]: preAuthStatusChip
}

const carrotCardBgColorByStates: Partial<
  Record<CardStates | "fallback", { tileColor: TileProps["color"]; iconColor: IconWithBackgroundProps["color"] }>
> = {
  [CardStates.ACTIVE]: { tileColor: "success", iconColor: "success" },
  [CardStates.DEDUCTIBLE_NOT_MET]: { tileColor: "error", iconColor: "error" },
  [CardStates.ELIGIBLE]: { tileColor: "info", iconColor: "info" },
  [CardStates.ELIGIBLE_WITH_DEDUCTIBLE_NOT_MET]: { tileColor: "info", iconColor: "info" },
  [CardStates.LOCKED]: { tileColor: "error", iconColor: "error" },
  [CardStates.NEEDS_ENROLLMENT]: { tileColor: "error", iconColor: "error" },
  [CardStates.PENDING]: { tileColor: "info", iconColor: "info" },
  [CardStates.RESTRICTION_PERIOD]: { tileColor: "error", iconColor: "error" },
  [CardStates.SHIPPED]: { tileColor: "info", iconColor: "info" },
  [CardStates.PRE_AUTH]: { tileColor: "success", iconColor: "success" },
  fallback: { tileColor: "info", iconColor: "info" }
}

export type CarrotCardModuleTemplateProps = {
  cardState?: CardStates
  title?: ReactNode
  body: ReactNode
  links?: ReactNode[]
}

export function CarrotCardModuleTemplate({
  cardState,
  title = <FormattedMessage defaultMessage="Carrot Card®" />,
  body,
  links = []
}: CarrotCardModuleTemplateProps) {
  const statusChip = carrotCardStatusByStates[cardState] || null
  const bgcolors = carrotCardBgColorByStates[cardState] || carrotCardBgColorByStates.fallback
  return (
    <Tile
      variant="secondary"
      color={bgcolors.tileColor}
      sx={{ display: "flex", flexDirection: "column", gap: (theme) => theme.spacing(theme.tokens.spacing.lg) }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <IconWithBackground color={bgcolors.iconColor} icon={CreditCardIcon} size="medium" />
        {statusChip}
      </Box>
      <Typography variant="h4">{title}</Typography>
      <Typography variant="body2">{body}</Typography>
      {links}
    </Tile>
  )
}
